import style from './NoteUpload.module.scss';
import { Button } from '@ax/ui/src/components/Button';
import { Auth } from '@ax/auth';
import { useEffect, useMemo, useState } from 'react';
import uploadEhrIcon from '@image/upload-ehr-icon.svg';
import sendToMdsIcon from '@image/send-to-mds-icon.svg';
import { useUploadEHRNoteApi } from '@/hooks/api/useUploadEHRNote';
import { usePatchNoteApi } from '@/hooks/api/usePatchNoteApi';
import {
  navigationSlice,
  noteHasPendingCareCuesResponses,
  selectProviderAppSettings,
  selectProviderProductOffering,
  selectProviderUserInfo,
  selectProviderUserSettings,
  setSelectedVisitDetails,
  useAppSelector,
  useDispatch,
  noteHSFormInvalidated,
  selectIsHCAHardStops,
  selectIsHSFormValid,
  selectIcd10CodeUploadEnabled,
  selectCurrentVisitStatus, selectIsReevalSentenceLoading,
} from '@ax/redux-store';
import { useGetNoteParagraphsAndSentences } from '@/hooks/api/useGetNoteContent';
import { NOTE_SECTION } from '@/constants/note-section';
import ReactDOMServer from 'react-dom/server';
import {
  ProviderProductOffering,
  ProviderProductOfferingStatus,
} from '@ax/service/src/provider-interface';
import toast from 'react-hot-toast';
import useAnalytics from '@/hooks/useAnalytics';
import {
  ANALYTICS_CONSTANTS,
  NOTE_STATUS,
  REMINDER_AVAILABILITY,
  NOTE_UPLOAD_METHOD,
  NotePageSections,
  NO_ICD_10_CODE,
  TemplateSelection,
  Code,
} from '@ax/constants';
import { format } from 'date-fns';
import { NOTE_SOURCE, NoteUpdate } from '@ax/service/src/ehr-interface';
import { useUploadDataLakeNoteApi } from '@/hooks/api/useUploadDataLake';
import CareCuesUploadModal from '@/modules/CareCues/CareCuesUploadModal';
import { useCareCuesModalDismissStatus } from '@/hooks/useCareCuesModalDismissStatus.tsx';
import { isAfter } from 'date-fns';
import { useMutation } from '@apollo/client';
import { ADD_FREE_TEXT } from '@ax/graphql';
import { AddFreeTextVariables } from '@/modules/Note/NoteContent.tsx';
import { isSingleSectionNote } from '@ax/redux-store';
import { Loader } from '@ax/ui';
import { recordingState } from '@/store/recordingState.tsx';
import { useSignalEffect } from '@preact/signals-react';
import ConfirmationModal from '@/components/ConfirmationModal';
import { UpdateNotePartVariables } from '@/constants/update-note-part-variables.ts';
import { UPDATE_NOTE_PART } from '@ax/graphql';
import { selectNoteMarkdownContent } from '@ax/redux-store';
import { statusHistorySelector } from '@ax/redux-store';
import { selectIsEdEnabled } from '@ax/redux-store';
import { formatTimeToShortAMPM } from '@/utils/index.tsx';
import { convertUTCtoLocalTime } from '@/utils/index.tsx';

const NoteUpload = () => {
  const providerUserSettings = useAppSelector(selectProviderUserSettings);
  const providerAppSettings = useAppSelector(selectProviderAppSettings);
  const providerProductOffering = useAppSelector(selectProviderProductOffering);
  const providerUserInfo = useAppSelector(selectProviderUserInfo);
  const { mutateAsync: patchNote } = usePatchNoteApi();
  const { mutateAsync: uploadEHRNote } = useUploadEHRNoteApi();
  const { mutateAsync: uploadDataLakeNote } = useUploadDataLakeNoteApi();
  const templates = useAppSelector((state) => state.note.templates);
  const visitSelected = useAppSelector((state) => state.selectedVisit);
  const noteStatus = useAppSelector(selectCurrentVisitStatus(visitSelected.uuid));
  const selectedVisitDate = useAppSelector((state) => state.selectedDate);
  const isIcd10CodeUploadEnabled = useAppSelector(selectIcd10CodeUploadEnabled);
  const isNoteSingleSection = useAppSelector(isSingleSectionNote);
  const noteSource = useAppSelector((state) => state.selectedVisit?.source);
  const { isModalDismissed, setModalDismissed } = useCareCuesModalDismissStatus(
    Auth.currentUser()?.uid,
  );
  const [addFreeText] = useMutation<{ addFreeText: any }, AddFreeTextVariables>(ADD_FREE_TEXT);
  const [isCareCuesUploadModalOpen, setIsCareCuesUploadModalOpen] = useState(false);
  const isReevalSentenceLoading = useAppSelector(selectIsReevalSentenceLoading);
  const isHSFormValid = useAppSelector(selectIsHSFormValid);
  const isHCAHardStops = useAppSelector(selectIsHCAHardStops);

  const noteMarkdownContent = useAppSelector(selectNoteMarkdownContent);
  const statusHistoryFiltered = useAppSelector(statusHistorySelector);
  const isEdEnabled = useAppSelector(selectIsEdEnabled);

  const [isRecordingInProgressModalOpen, setIsRecordingInProgressModalOpen] = useState(false);
  const [isRecordingInprogress, setIsRecordingInprogress] = useState(false);

  const {
    hpiSectionParagraphs,
    intervalHistoryParagraphs,
    rosSentences,
    peSentences,
    apSectionParagraphs,
    mdmSectionParagraphs,
    progressSentences,
    hpiSectionSentences,
    intervalHistorySectionSentences,
    apSectionSentences,
    rosSectionSentences,
    wholeNoteSentences,
    wholeNoteSectionSentences,
    peSectionSentences,
    mdmSectionSentences,
    progressSectionSentences,
  } = useGetNoteParagraphsAndSentences({ noteId: visitSelected.uuid, style, noteStatus });

  const analytics = useAnalytics();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isUploadingToMds, setIsUploadingToMds] = useState(false);
  const [isUploadingToEhr, setIsUploadingToEhr] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('');

  const [updateNotePart] = useMutation<{ updateNotePart: any }, UpdateNotePartVariables>(
    UPDATE_NOTE_PART,
  );

  useEffect(() => {
    if (
      providerProductOffering &&
      providerUserInfo &&
      providerUserSettings &&
      providerAppSettings
    ) {
      setIsDataLoaded(true);
    }
  }, [providerProductOffering, providerUserInfo, providerUserSettings, providerAppSettings]);

  useSignalEffect(() => {
    setIsRecordingInprogress(recordingState.value.recordingInprogress);
  });

  const setIsUploading = (method: NOTE_UPLOAD_METHOD, value: boolean) => {
    if (method === NOTE_UPLOAD_METHOD.MDS_BUTTON) {
      setIsUploadingToMds(value);
    }
    if (method === NOTE_UPLOAD_METHOD.EHR_BUTTON) {
      setIsUploadingToEhr(value);
    }
  };

  const ehrIntegrated = providerAppSettings?.ehrIntegrationEnabled;
  const ehrUploadEnabled = providerAppSettings?.ehrUploadNoteEnabled;
  const dataLakeUploadEnabled = providerAppSettings?.clientDataLakeUploadEnabled;
  const careCuesEnabled = providerUserSettings?.enabledNudges;

  const goAssistPackage =
    providerProductOffering?.name === ProviderProductOffering.GO_ASSIST &&
    providerProductOffering?.status === ProviderProductOfferingStatus.ACTIVE;
  const goUploadPackage =
    providerProductOffering?.name === ProviderProductOffering.GO_UPLOAD &&
    providerProductOffering?.status === ProviderProductOfferingStatus.ACTIVE;
  const goAssistSendToMDSForUpload =
    providerProductOffering?.settings?.disableSendToMdsForUploadOnly;

  const dispatch = useDispatch();

  const hasPendingCareCuesResponses = useAppSelector(
    noteHasPendingCareCuesResponses(visitSelected.uuid),
  );

  const handleUpdateVisitDetails = (noteStatus: number) => {
    dispatch(setSelectedVisitDetails({ ...visitSelected, noteStatus }));
  };

  const getAnalyticsProperties = (uploadMethod: string, errorMessage = undefined) => {
    return {
      noteId: visitSelected.uuid,
      visitDateTime: `${selectedVisitDate?.date}, ${visitSelected.appointmentStartTime}`,
      eventDateTime: format(new Date(), 'MM/dd/yyyy HH:mm:ss'),
      providerGuid: Auth.currentUser()?.guid,
      providerUid: Auth.currentUser()?.uid,
      providerName: providerUserInfo?.name,
      providerEmail: providerUserInfo?.email,
      packageAssigned: providerProductOffering?.name,
      uploadMethod,
      errorMessage,
    };
  };

  const addCodesToSectionTemplates = async () => {
    const allSentences = [
      ...hpiSectionSentences,
      ...intervalHistorySectionSentences,
      ...apSectionSentences,
      ...rosSectionSentences,
      ...peSectionSentences,
      ...mdmSectionSentences,
      ...progressSectionSentences,
    ];

    for (const section of allSentences) {
      const { Section, ComplaintId } = section;
      const firstSentence = section.sentences[0];

      const matchingStateTemplate = templates.find(
        (template: TemplateSelection) =>
          template.complaintId === ComplaintId && template.section === Section,
      );

      if (!matchingStateTemplate || matchingStateTemplate.codes.length === 0) continue;

      const selectedCode = matchingStateTemplate.codes.find(
        (code: Code) => code.status === 'ACCEPTED',
      );

      if (!selectedCode) continue;

      const codeDisplayText =
        selectedCode.code !== NO_ICD_10_CODE
          ? `${selectedCode.display}-${selectedCode.code}`
          : selectedCode.display;
      if (firstSentence.Sentence.trim() !== codeDisplayText) {
        const codeSentence = {
          noteId: visitSelected.uuid,
          Content: codeDisplayText,
          Order: 0,
          SharedId: visitSelected.uuid,
          ActionSource: 'User',
          Sentence: codeDisplayText,
          ComplaintId: ComplaintId,
          Section: Section,
        };
        await addFreeText({ variables: codeSentence as AddFreeTextVariables });
      }
    }
  };

  const onUploadEHRNote = async (noteEditingEnabled = false) => {
    let noteStatus = NOTE_STATUS.NOTE_UPLOAD_FAILED; // Default to failed status
    try {
      const res = await uploadEHRNote({
        appointmentUuid: visitSelected.uuid,
        updates: noteUpdates(),
      });
      if (res.code === '000') {
        noteStatus = NOTE_STATUS.NOTE_UPLOADED_TO_EHR;
        analytics.onNoteUploaded(
          getAnalyticsProperties(ANALYTICS_CONSTANTS.uploadMethods.uploadToEhr),
        );
      } else {
        analytics.onNoteUploadFailed(
          getAnalyticsProperties(ANALYTICS_CONSTANTS.uploadMethods.uploadToEhr, res?.message),
        );
      }
    } catch (error) {
      console.log(error);
    }

    try {
      // Attempt to call patchNote with the determined status
      const res = await patchNote({
        appointmentId: visitSelected.uuid,
        noteStatus,
        noteEditingEnabled,
      });
      res.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_EHR
        ? (noteStatus = NOTE_STATUS.NOTE_UPLOADED_TO_EHR)
        : NOTE_STATUS.NOTE_UPLOAD_FAILED;

      // After uploading to EHR, update note to datalake if flag is enabled.
      if (res.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_EHR && dataLakeUploadEnabled) {
        await onUploadDataLakeNote();
      }
    } catch (error) {
      // Log a message if patchNote execution fails
      console.log('Failed to update note status:', error);
    }
    // dispatch the updated note status to the store
    handleUpdateVisitDetails(noteStatus);
    // Display a toast message based on the note status
    noteStatus === NOTE_STATUS.NOTE_UPLOAD_FAILED
      ? toast.error('An error occurred, please try again')
      : toast.success('Note uploaded successfully');
  };

  const onSendToMDS = async (noteEditingEnabled = false) => {
    let noteStatus = NOTE_STATUS.NOTE_UPLOAD_FAILED;

    try {
      const res = await patchNote({
        appointmentId: visitSelected.uuid,
        noteStatus: NOTE_STATUS.NOTE_UPLOADED_TO_MDS,
        noteEditingEnabled,
      });
      if (!res.error) {
        noteStatus = NOTE_STATUS.NOTE_UPLOADED_TO_MDS;
        const uploadMethod = noteEditingEnabled
          ? ANALYTICS_CONSTANTS.uploadMethods.mdsEditAndUpload
          : ANALYTICS_CONSTANTS.uploadMethods.mdsUploadOnly;
        analytics.onNoteUploaded(getAnalyticsProperties(uploadMethod));
      } else {
        analytics.onNoteUploadFailed(
          getAnalyticsProperties(ANALYTICS_CONSTANTS.uploadMethods.mdsUploadOnly, res?.message),
        );
      }
    } catch (error) {
      console.log(error);
    }
    // dispatch the updated note status to the store
    handleUpdateVisitDetails(noteStatus);
    // Display a toast message based on the note status
    noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_MDS
      ? toast.success('Note uploaded successfully')
      : toast.error('An error occurred, please try again');
  };

  const onUploadDataLakeNote = async () => {
    try {
      await uploadDataLakeNote({
        appointmentUuid: visitSelected.uuid,
        updates: noteUpdates(),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const onHandleEHRUpload = async () => {
    if (!ehrIntegrated || noteSource === NOTE_SOURCE.MANUAL || !ehrUploadEnabled) {
      await onSendToMDS();
    } else {
      await onUploadEHRNote();
    }
  };

  const handleUploadAnyway = async (doNotShowAgain: boolean) => {
    if (doNotShowAgain) {
      setModalDismissed();
    }
    await handleUploadMethod(uploadMethod);
    setUploadMethod('');
  };

  const handleReviewReminders = (doNotShowAgain: boolean) => {
    if (doNotShowAgain) {
      setModalDismissed();
    }

    dispatch(
      navigationSlice.actions.sectionNavigationRequested({
        section: NotePageSections.CareCues,
        noteId: visitSelected.uuid,
      }),
    );
    setIsUploading(uploadMethod, false);
    setUploadMethod('');
  };

  const initiateUploadProcess = async (method: NOTE_UPLOAD_METHOD) => {
    if (isUploadingToEhr || isUploadingToMds) {
      return;
    }

    try {
      setIsUploading(method, true);

      if (
        careCuesEnabled &&
        visitSelected.reminderAvailability === REMINDER_AVAILABILITY.AVAILABLE &&
        !isModalDismissed
      ) {
        setUploadMethod(method);
        setIsCareCuesUploadModalOpen(true);
        return;
      }

      if (isHCAHardStops && !isHSFormValid) {
        dispatch(noteHSFormInvalidated({ noteId: visitSelected.uuid }));
        return;
      }

      if (isRecordingInprogress) {
        setUploadMethod(method);
        setIsRecordingInProgressModalOpen(true);
        return;
      }

      await handleUploadMethod(method);
    } catch (error) {
      console.error(error);
      toast.error('An error occurred processing the note upload.');
    } finally {
      setIsUploading(method, false);
    }
  };

  const appendProgressSentencesToWholeNoteSentence = async () => {
    const progressSentencesAfterUpload: string[] = [];
    progressSectionSentences.forEach((sentence) => {
      sentence.sentences.forEach((sentenceContent) => {
        const lastStatus = statusHistoryFiltered[statusHistoryFiltered.length - 1];
        if (
          !lastStatus?.createdAt ||
          isAfter(new Date(sentenceContent.CreatedAt), new Date(lastStatus.createdAt))
        ) {
          progressSentencesAfterUpload.push(
            `#### Progress #${sentenceContent.Order}\n` +
              `#### Time ${formatTimeToShortAMPM(
                convertUTCtoLocalTime(sentenceContent.CreatedAt),
              )}\n` +
              sentenceContent.Sentence +
              '\n\n',
          );
        }
      });
    });
    let updatedWholeNoteSentence = wholeNoteSectionSentences[0].sentences[0].Sentence + '\n\n';
    progressSentencesAfterUpload.forEach((sentence) => {
      updatedWholeNoteSentence += sentence;
    });

    await updateNotePart({
      variables: {
        noteId: visitSelected.uuid,
        SharedId: visitSelected.uuid,
        selection: noteMarkdownContent.selection,
        Content: updatedWholeNoteSentence,
        ActionSource: 'User',
        UpdatedAt: new Date().toISOString(),
      },
    });
  };

  const handleUploadMethod = async (method: NOTE_UPLOAD_METHOD) => {
    await appendProgressSentencesToWholeNoteSentence();

    try {
      if (isIcd10CodeUploadEnabled) {
        await addCodesToSectionTemplates();
      }

      switch (method) {
        case NOTE_UPLOAD_METHOD.EHR_BUTTON:
          await onHandleEHRUpload();
          break;
        case NOTE_UPLOAD_METHOD.MDS_BUTTON:
          await onSendToMDS(true);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred uploading the note.');
    } finally {
      setIsUploading(method, false);
    }
  };

  const getNoteSectionParagraphsText = (sectionParagraphs: JSX.Element[]) => {
    let text: string = '';
    sectionParagraphs.forEach((paragraph: JSX.Element) => {
      text += ReactDOMServer.renderToString(paragraph);
    });

    return text;
  };

  const noteUpdates = () => {
    const updates = [
      {
        noteSection: NOTE_SECTION.hpi,
        replace: false,
        text: getNoteSectionParagraphsText(hpiSectionParagraphs),
      },
      {
        noteSection: NOTE_SECTION.intervalHistory,
        replace: false,
        text: getNoteSectionParagraphsText(intervalHistoryParagraphs),
      },
      {
        noteSection: NOTE_SECTION.ros,
        replace: false,
        text: getNoteSectionParagraphsText(rosSentences),
      },
      {
        noteSection: NOTE_SECTION.pe,
        replace: false,
        text: getNoteSectionParagraphsText(peSentences),
      },
      {
        noteSection: NOTE_SECTION.ap,
        replace: false,
        text: getNoteSectionParagraphsText(apSectionParagraphs),
      },
      {
        noteSection: NOTE_SECTION.mdm,
        replace: false,
        text: getNoteSectionParagraphsText(mdmSectionParagraphs),
      },
      {
        noteSection: NOTE_SECTION.progress,
        replace: false,
        text: getNoteSectionParagraphsText(progressSentences),
      },
    ];

    if (isNoteSingleSection) {
      return [
        {
          noteSection: NOTE_SECTION.whole_note,
          replace: false,
          text: getNoteSectionParagraphsText(wholeNoteSentences),
        },
      ];
    }

    return updates.filter((update) => update.text !== '');
  };

  const containsNonEmptyText = (updates: NoteUpdate[]) => {
    return updates.some((update: NoteUpdate) => {
      // Remove HTML tags and trim the remaining text
      const textWithoutTags = update.text.replace(/<[^>]*>/g, '').trim();
      // Check if the resulting string is not empty
      return textWithoutTags.length > 0;
    });
  };

  const buttonsDisabled = useMemo(() => {
    let newSentenceContent = false;
    let sentences = [
      ...hpiSectionSentences,
      ...intervalHistorySectionSentences,
      ...rosSectionSentences,
      ...peSectionSentences,
      ...apSectionSentences,
      ...mdmSectionSentences,
      ...progressSectionSentences,
    ]
      .map((section) => section.sentences)
      .flat();

    if (selectedVisitDate.date > new Date().toISOString()) {
      return true;
    }

    if (isNoteSingleSection) {
      sentences = [...progressSectionSentences, ...wholeNoteSectionSentences]
        .map((section) => section.sentences)
        .flat();
    }
    const lastStatus = statusHistoryFiltered[statusHistoryFiltered.length - 1];
    newSentenceContent = Boolean(
      sentences.find((sentence) => {
        if (!lastStatus?.createdAt) {
          return true;
        }
        return isAfter(new Date(sentence?.CreatedAt), new Date(lastStatus?.createdAt));
      }),
    );

    return (
      (!newSentenceContent &&
        (visitSelected.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_EHR ||
          visitSelected.noteStatus === NOTE_STATUS.NOTE_UPLOADED_TO_MDS ||
          visitSelected.noteStatus === NOTE_STATUS.CONTENT_PROCESSING ||
          noteUpdates().length === 0 ||
          !containsNonEmptyText(noteUpdates()))) ||
      isReevalSentenceLoading ||
      hasPendingCareCuesResponses
    );
  }, [
    selectedVisitDate.date,
    hpiSectionSentences,
    intervalHistorySectionSentences,
    rosSectionSentences,
    peSectionSentences,
    apSectionSentences,
    mdmSectionSentences,
    progressSectionSentences,
    wholeNoteSectionSentences,
    visitSelected,
    noteUpdates,
    hasPendingCareCuesResponses,
    isNoteSingleSection,
  ]);

  const handleOnIsRecordingInProgressModalContinue = async () => {
    setIsRecordingInProgressModalOpen(false);
    setIsRecordingInprogress(false);
    await handleUploadMethod(uploadMethod);
    setUploadMethod('');
  };

  const handleOnIsRecordingInProgressModalClose = () => {
    setIsRecordingInProgressModalOpen(false);
    setIsUploadingToEhr(false);
    setIsUploadingToMds(false);
  };

  return (
    <>
      {/* Other components and logic */}
      {
        <CareCuesUploadModal
          modalOpen={isCareCuesUploadModalOpen}
          modalClose={() => {
            setIsCareCuesUploadModalOpen(false);
            setIsUploading(uploadMethod, false);
            setUploadMethod('');
          }}
          onUploadAnyway={(value) => {
            handleUploadAnyway(value).catch((error) => console.log(error));
          }}
          onReviewReminders={handleReviewReminders}
          noteId={visitSelected.uuid}
        />
      }
      {
        <ConfirmationModal
          modalOpen={isRecordingInProgressModalOpen}
          modalConfirm={() => {
            handleOnIsRecordingInProgressModalContinue();
          }}
          modalClose={() => {
            handleOnIsRecordingInProgressModalClose();
          }}
        />
      }
      {/* Upload to EHR */}
      <div className={style.container}>
        {isDataLoaded && !(goAssistPackage && goAssistSendToMDSForUpload) && (
          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={() => initiateUploadProcess(NOTE_UPLOAD_METHOD.EHR_BUTTON)}
            disabled={buttonsDisabled}
          >
            <img
              alt={'mds-upload-icon'}
              className={style.visit__list__item__content__icon}
              src={uploadEhrIcon}
              height={18}
            />
            {isUploadingToEhr ? <Loader color={'white'} /> : 'Upload to EHR'}
          </Button>
        )}
        {/* Send to MDS */}
        {!isEdEnabled && isDataLoaded && goAssistPackage && !goUploadPackage && (
          <Button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={() => initiateUploadProcess(NOTE_UPLOAD_METHOD.MDS_BUTTON)}
            disabled={buttonsDisabled}
          >
            <img
              alt={'mds-upload-icon'}
              className={style.visit__list__item__content__icon}
              src={sendToMdsIcon}
              height={18}
            />
            {isUploadingToMds ? <Loader color={'white'} /> : 'Send to MDS'}
          </Button>
        )}
      </div>
    </>
  );
};

export default NoteUpload;
